import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "../../components/common";
import Image from "./Image";
import { CircularProgress } from "@material-ui/core";

type CommonVideoModalProps = {
  openModal: boolean;
  videoUrl: string;
  onClose?: () => void;
  onCoverSelect?: (cover: { time: number; blob: Blob }) => void;
};

type Thumbnail = {
  time: number;
  blob: Blob;
};

const CommonVideoModal: React.FC<CommonVideoModalProps> = ({
  openModal,
  videoUrl,
  onClose,
  onCoverSelect,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const thumbsBarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [startTime, setStartTime] = useState<number>(0);
  const [endTime, setEndTime] = useState<number>(0);
  const [thumbnails, setThumbnails] = useState<Thumbnail[]>([]);

  const [currentTime, setCurrentTime] = useState(0);
  const [playheadPos, setPlayheadPos] = useState(0);
  const [srcToUse, setSrcToUse] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setThumbnails([]);

    thumbnails.forEach((thumb) => {
      URL.revokeObjectURL(URL.createObjectURL(thumb.blob));
    });

    if (typeof onClose === "function") {
      onClose();
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    video.pause();
  }, []);

  async function fetchWithCORS(url: string): Promise<Blob> {
    try {
      const response = await fetch(url, {
        mode: "cors",
        headers: {
          Range: "bytes=0-",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error("Error fetching video:", error);
      throw new Error(`Failed to fetch video: ${error.message}`);
    }
  }

  useEffect(() => {
    if (!openModal) return;

    const loadVideo = async () => {
      setIsLoading(true);
      try {
        if (/^https?:\/\//.test(videoUrl)) {
          const blob = await fetchWithCORS(videoUrl);
          const blobUrl = URL.createObjectURL(blob);
          setSrcToUse(blobUrl);
        } else {
          setSrcToUse(videoUrl);
        }
      } catch (error) {
        console.error("Video loading error:", error);
        setSrcToUse(videoUrl);
      } finally {
        setIsLoading(false);
      }
    };

    loadVideo();

    return () => {
      if (srcToUse.startsWith("blob:")) {
        URL.revokeObjectURL(srcToUse);
      }
    };

    // eslint-disable-next-line
  }, [openModal, videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !srcToUse) return;

    const handleLoaded = () => {
      video.currentTime = 0;
      video.pause();
    };

    const handleError = () => {
      console.error("Video error:", video.error);
      // Fallback to direct URL if blob fails
      if (srcToUse.startsWith("blob:")) {
        setSrcToUse(videoUrl);
      }
    };

    video.addEventListener("loadedmetadata", handleLoaded);
    video.addEventListener("error", handleError);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoaded);
      video.removeEventListener("error", handleError);
    };

    // eslint-disable-next-line
  }, [srcToUse]);

  const updatePlayheadPosition = (time: number) => {
    if (!thumbsBarRef.current) return;

    const barWidth = thumbsBarRef.current.offsetWidth;
    const clampedTime = Math.min(Math.max(time, startTime), endTime);
    const relativeTime = clampedTime - startTime;
    const duration = endTime - startTime || 1; // avoid divide by zero
    const pos = (relativeTime / duration) * barWidth;

    setPlayheadPos(pos);
  };

  const handlePlayheadMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!thumbsBarRef.current || !videoRef.current) return;

    const bar = thumbsBarRef.current;
    const video = videoRef.current;
    const duration = endTime - startTime || 1;

    const onMove = (moveEvent: MouseEvent) => {
      let rect = bar.getBoundingClientRect();
      let x = moveEvent.clientX - rect.left;
      x = Math.max(0, Math.min(x, bar.offsetWidth));

      const newTime = startTime + (x / bar.offsetWidth) * duration;
      video.currentTime = newTime;
      video.pause();
      setCurrentTime(newTime);
      setPlayheadPos(x);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onLoadedMetadata = async () => {
    const video = videoRef.current;
    const bar = thumbsBarRef.current;
    if (!video || !bar) return;

    setThumbnails([]);
    setStartTime(0);
    setEndTime(video.duration);

    const aspectRatio = video.videoWidth / video.videoHeight;
    const thumbnailHeight = 50;
    const exactThumbWidth = thumbnailHeight * aspectRatio;
    const containerWidth = bar.offsetWidth;
    const exactCount = Math.ceil(containerWidth / exactThumbWidth);

    await generateThumbnails(video, exactCount, (thumb) => {
      setThumbnails((prev) => [...prev, thumb]);
    });

    video.currentTime = 0;
    video.pause();
    setCurrentTime(0);
    updatePlayheadPosition(0);
  };

  const generateThumbnails = async (
    video: HTMLVideoElement,
    count: number,
    onThumbnailReady: (thumb: Thumbnail) => void,
    maxSizeKB = 15,
    maxWidth = 320
  ): Promise<void> => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return;

    video.crossOrigin = "anonymous";
    const targetBytes = maxSizeKB * 1024;

    const scale = Math.min(1, maxWidth / video.videoWidth);
    canvas.width = Math.floor(video.videoWidth * scale);
    canvas.height = Math.floor(video.videoHeight * scale);

    const duration = video.duration;
    const times = Array.from({ length: count }, (_, i) =>
      parseFloat(((duration / count) * i).toFixed(2))
    );

    for (const time of times) {
      await seekVideo(video, time);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      let blob: Blob | null = null;
      let quality = 0.85;
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        const currentQuality = quality;
        blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(
            (result) => resolve(result),
            "image/jpeg",
            currentQuality
          );
        });

        if (!blob) break;
        if (blob.size <= targetBytes) break;

        quality = Math.max(0.1, quality * 0.7);
        attempts++;
      }

      if (blob && blob.size > targetBytes) {
        let newWidth = Math.floor(canvas.width * 0.8);
        let newHeight = Math.floor(canvas.height * 0.8);
        let minDimensionReached = false;

        while (!minDimensionReached && blob.size > targetBytes) {
          if (newWidth < 64 || newHeight < 64) {
            minDimensionReached = true;
            break;
          }

          canvas.width = newWidth;
          canvas.height = newHeight;
          context.drawImage(video, 0, 0, newWidth, newHeight);

          blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((result) => resolve(result), "image/jpeg", 0.6);
          });

          if (!blob) break;

          if (blob.size > targetBytes) {
            newWidth = Math.floor(newWidth * 0.8);
            newHeight = Math.floor(newHeight * 0.8);
          }
        }
      }

      if (blob) {
        onThumbnailReady({ time, blob });
      }
    }
  };

  const selectCover = async () => {
    const video = videoRef.current;
    if (!video) return;

    await seekVideo(video, currentTime);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const maxSizeKB = 15;
    const targetBytes = maxSizeKB * 1024;
    const maxWidth = 320;

    const scale = Math.min(1, maxWidth / video.videoWidth);
    canvas.width = Math.floor(video.videoWidth * scale);
    canvas.height = Math.floor(video.videoHeight * scale);

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    let blob: Blob | null = null;
    let quality = 0.85;
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      const currentQuality = quality;
      blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (result) => resolve(result),
          "image/jpeg",
          currentQuality
        );
      });

      if (!blob) break;

      if (blob.size <= targetBytes) break;

      quality = Math.max(0.1, quality * 0.7);
      attempts++;
    }

    if (blob && blob.size > targetBytes) {
      let newWidth = Math.floor(canvas.width * 0.8);
      let newHeight = Math.floor(canvas.height * 0.8);
      let minDimensionReached = false;

      while (!minDimensionReached && blob.size > targetBytes) {
        if (newWidth < 64 || newHeight < 64) {
          minDimensionReached = true;
          break;
        }

        canvas.width = newWidth;
        canvas.height = newHeight;
        ctx.drawImage(video, 0, 0, newWidth, newHeight);

        blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((result) => resolve(result), "image/jpeg", 0.6);
        });

        if (!blob) break;

        if (blob.size > targetBytes) {
          newWidth = Math.floor(newWidth * 0.8);
          newHeight = Math.floor(newHeight * 0.8);
        }
      }
    }

    if (blob && onCoverSelect) {
      onCoverSelect({ time: currentTime, blob });
    }

    if (onClose) onClose();
  };
  const seekVideo = (video: HTMLVideoElement, time: number) => {
    return new Promise<void>((resolve) => {
      const onSeeked = () => {
        video.removeEventListener("seeked", onSeeked);
        resolve();
      };
      video.addEventListener("seeked", onSeeked);
      video.currentTime = time;
    });
  };

  useEffect(() => {
    setThumbnails([]);
    setCurrentTime(0);
    setPlayheadPos(0);

    return () => {
      thumbnails.forEach((thumb) => {
        URL.revokeObjectURL(URL.createObjectURL(thumb.blob));
      });
      setThumbnails([]);
    };
    // eslint-disable-next-line
  }, [videoUrl, openModal]);

  const handleThumbsBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!thumbsBarRef.current || !videoRef.current) return;

    const bar = thumbsBarRef.current;
    const video = videoRef.current;
    const rect = bar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const frac = clickX / rect.width;
    const time = startTime + frac * (endTime - startTime);

    video.currentTime = time;
    video.pause();
    setCurrentTime(time);
    updatePlayheadPosition(time);
  };

  return (
    <Modal
      visible={openModal}
      closeOnBackDrop={true}
      onClose={handleClose}
      className="common-video-modal"
      title="Select Cover Image"
      size="x-large"
    >
      <div className="video-modal-content" ref={containerRef}>
        {/* <div className="video-player-container">
          <video
            ref={videoRef}
            className="video-player"
            controls
            src={srcToUse}
            crossOrigin="anonymous"
            onLoadedMetadata={onLoadedMetadata}
          />
        </div> */}
        {isLoading ? (
          <div className="video-player-container">
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="video-player-container">
              <video
                ref={videoRef}
                className="video-player"
                controls
                src={srcToUse}
                crossOrigin="anonymous"
                onLoadedMetadata={onLoadedMetadata}
              />
            </div>
            {/* Rest of your modal content */}
          </>
        )}

        <div className="video-thumbs-bar " onClick={handleThumbsBarClick}>
          <div
            className={`main-div-thumb ${isLoading ? "loading-text" : ""}`}
            ref={thumbsBarRef}
          >
            {isLoading ? (
              <p>Generathing Thumbnails.....</p>
            ) : (
              <>
                {thumbnails.map((thumb, index) => (
                  <Image
                    key={index}
                    src={URL.createObjectURL(thumb.blob)}
                    alt={`Thumbnail ${index + 1}`}
                    className="thumb-image"
                  />
                ))}
              </>
            )}
          </div>

          <div
            onMouseDown={handlePlayheadMouseDown}
            className="playhead"
            style={{
              left: playheadPos + 10,
            }}
          />
        </div>

        <div className="d-flex justify-content-center mt-30">
          <Button
            type="submit"
            color="primary"
            className="submit-button text-uppercase"
            onClick={selectCover}
          >
            Select Cover
          </Button>
          <Button
            variant="text"
            color="secondary"
            className="cancel-button text-uppercase ml-10"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CommonVideoModal;
